import React, { useEffect, useState, useRef } from 'react';
import { collection, getDocs, deleteDoc, doc, setDoc, getDoc, updateDoc, arrayUnion, where, query } from 'firebase/firestore';
import { db } from '../firebase';
import { Bars3Icon } from '@heroicons/react/24/solid';
import { showSnackbar } from './SnackbarAlert';
import { startOfMonth, endOfMonth, subDays, addDays } from 'date-fns';

const ConversationList = ({ conversations, setConversations, onSelectConversation, selectedConversation, setStartDate, setEndDate, startDate, endDate }) => {

  const now = new Date();;

  const [conversationTags, setConversationTags] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [menuOpen, setMenuOpen] = useState(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [includeTags, setIncludeTags] = useState([]);
  const [excludeTags, setExcludeTags] = useState([]);
  const menuRefs = useRef({});
  const [includeDropdownOpen, setIncludeDropdownOpen] = useState(false);
  const [excludeDropdownOpen, setExcludeDropdownOpen] = useState(false);
  const [idFilter, setIdFilter] = useState('');


  const handleMonthYearChange = (event) => {
    const [year, month] = event.target.value.split('-');
    const selectedDate = new Date(year, month - 1);

    const newStartDate = startOfMonth(selectedDate);
    const newEndDate = endOfMonth(selectedDate);

    setStartDate(newStartDate); // Pass updated values to parent
    setEndDate(newEndDate);
  };

  useEffect(() => {
    const fetchTagsForConversations = async () => {
      try {
        // Calculate the starting date using date-fns
        const tagsData = {};

        // Query Firestore for tags with a timestamp >= startDate
        const tagsRef = collection(db, 'tags');
        const q = query(
          tagsRef, 
          where('timestamp', '>=', subDays(startDate, 5)), 
          where('timestamp', '<=', addDays(endDate, 5))
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((docSnapshot) => {
          if (docSnapshot.exists()) {
            tagsData[docSnapshot.id] = docSnapshot.data().tags || [];
          } else {
            console.warn(`No tag document found for ID: ${docSnapshot.id}`);
          }
        });

        setConversationTags(tagsData); // Update state with fetched tags
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      }
    };

    fetchTagsForConversations();
  }, [endDate, startDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen &&
        menuRefs.current[menuOpen] &&
        !menuRefs.current[menuOpen].contains(event.target)
      ) {
        setMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleDeleteConfirm = async () => {
    if (selectedConversationId) {
      try {
        await deleteDoc(doc(db, 'conversations', selectedConversationId));
        setConversations((prevConversations) =>
          prevConversations.filter((conversation) => conversation.id !== selectedConversationId)
        );
        showSnackbar('Conversation deleted successfully', 'success');
      } catch (error) {
        showSnackbar('Failed to delete conversation', 'error');
      }
      setDialogOpen(false);
    } else {
      console.error('No conversation selected for deletion');
    }
  };

  const handleAddTagConfirm = async () => {
    if (selectedConversationId && selectedTag) {
      try {
        // Reference to the document in the `tags` collection
        const tagsDocRef = doc(db, 'tags', selectedConversationId);
        const tagsDocSnapshot = await getDoc(tagsDocRef);
  
        if (tagsDocSnapshot.exists()) {
          const tagsData = tagsDocSnapshot.data();
          const currentTags = tagsData.tags || [];
  
          if (currentTags.includes(selectedTag)) {
            showSnackbar('Tag already exists', 'info');
          } else {
            // Update the `tags` array in the existing document
            await updateDoc(tagsDocRef, {
              tags: arrayUnion(selectedTag),
            });
            showSnackbar(`Tag added: ${selectedTag}`, 'success');
            setConversationTags((prevTags) => ({
              ...prevTags,
              [selectedConversationId]: [...currentTags, selectedTag],
            }));
          }
        } else {
          // Find the conversation's timestamp from the conversations prop
          const conversation = conversations.find((c) => c.id === selectedConversationId);
          const conversationTimestamp = conversation?.timestamp || null;
  
          // Create the new `tags` document with the conversation's timestamp
          await setDoc(tagsDocRef, {
            conversation_id: selectedConversationId,
            tags: [selectedTag],
            timestamp: conversationTimestamp, // Use the timestamp from the conversation
          });
  
          showSnackbar(`Tag added: ${selectedTag}`, 'success');
          setConversationTags((prevTags) => ({
            ...prevTags,
            [selectedConversationId]: [selectedTag],
          }));
        }
      } catch (error) {
        console.error('Failed to add tag:', error);
        showSnackbar('Failed to add tag', 'error');
      }
    } else {
      console.error('No conversation or tag selected');
    }
  
    // Reset dialog and input state
    setDialogOpen(false);
    setSelectedTag('');
  };
  

  const handleMenuClick = (option, conversationId) => {
    setDialogType(option);
    setSelectedConversationId(conversationId); 
    setDialogOpen(true);
    setMenuOpen(null); 
  };

  const toggleMenu = (conversationId) => {
    setMenuOpen(menuOpen === conversationId ? null : conversationId);
  };

  const handleTagFilter = (tag, type) => {
    if (type === 'include') {
      setIncludeTags((prev) =>
        prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
      );
    } else {
      setExcludeTags((prev) =>
        prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
      );
    }
  };

  const filteredConversations = conversations.filter((conversation) => {
    const tags = conversationTags[conversation.id] || [];
    const hasIncludeTags = includeTags.every((tag) => tags.includes(tag));
    const hasExcludeTags = excludeTags.every((tag) => !tags.includes(tag));
    const matchesIdFilter = 
    !idFilter || 
    (String(conversation.id_simple).includes(idFilter));
    return hasIncludeTags && hasExcludeTags && matchesIdFilter;
  });

  return (
    <div className="w-full bg-gray-100 p-4">
      <h2 className="text-lg font-bold mb-4">Conversations</h2>

      {/* Filter Section */}
      <div className="mb-4">
        <h3 className="font-semibold">Filter by Month</h3>
        <div className="mt-2 mb-3">
          <input
            id="monthYearPicker"
            type="month"
            className="p-2 border border-gray-300 rounded-md"
            onChange={handleMonthYearChange}
            defaultValue={`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`}
          />
        </div>
        <h3 className="font-semibold">Filter by Tags</h3>
        <div className="flex space-x-4 mt-2">

          {/* Include Tags Dropdown */}
          <div className="relative z-40">
            <button
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white shadow-sm hover:bg-gray-100 focus:outline-none"
              onClick={() => setIncludeDropdownOpen(!includeDropdownOpen)}
            >
              Include Tags
            </button>
            {includeDropdownOpen && (
              <div className="absolute mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                {['mercately', 'hand-off', 'WA','cotiza', 'internal', 'internal_check', 'pregunta', 'seguridad', 'profanity', 'extraordinario',
                'Uber', 'Plataforma', 'Particular', 'finalizado' 
                ].map((tag) => (
                  <label
                    key={tag}
                    className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={includeTags.includes(tag)}
                      onChange={() => handleTagFilter(tag, 'include')}
                    />
                    {tag}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Exclude Tags Dropdown */}
          <div className="relative z-40">
            <button
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white shadow-sm hover:bg-gray-100 focus:outline-none"
              onClick={() => setExcludeDropdownOpen(!excludeDropdownOpen)}
            >
              Exclude Tags
            </button>
            {excludeDropdownOpen && (
              <div className="absolute mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                {['mercately', 'hand-off', 'WA','cotiza', 'internal', 'internal_check', 'pregunta', 'seguridad', 'profanity', 'extraordinario',
                'Uber', 'Plataforma', 'Particular', 'finalizado' 
                ].map((tag) => (
                  <label
                    key={tag}
                    className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={excludeTags.includes(tag)}
                      onChange={() => handleTagFilter(tag, 'exclude')}
                    />
                    {tag}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <h3 className="font-semibold mt-4">Filter by ID</h3>
        <div className="mt-2">
          <input
            type="text"
            placeholder="Enter ID"
            className="p-2 border border-gray-300 rounded-md w-full"
            value={idFilter}
            onChange={(e) => setIdFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Conversation List */}
      <ul className="space-y-2">
        {filteredConversations.map((conversation) => (
          <li
            key={conversation.id}
            onClick={() => onSelectConversation(conversation)}
            className={`relative cursor-pointer p-2 rounded-lg shadow transition flex flex-col justify-center ${
              selectedConversation && selectedConversation.id === conversation.id
                ? 'bg-[#4790CF] text-white'
                : 'bg-white hover:bg-gray-200 text-gray-800'
            }`}
            style={{ minWidth: '100%' }}
          >
            <span className="truncate">ID: {conversation.id_simple ? conversation.id_simple : conversation.id.substring(0, 8) + "..."}</span>
            <span
              className={`text-sm ${
                selectedConversation && selectedConversation.id === conversation.id
                  ? 'text-white'
                  : 'text-gray-500'
              }`}
            >
              {new Date(conversation.timestamp?.toDate()).toLocaleString()}
            </span>

            <div className="flex flex-wrap space-x-1 mt-2">
              {conversationTags[conversation.id]?.map((tag, index) => (
                <span
                  key={index}
                  className={`text-xs px-2 py-1 rounded-full mt-1 ${
                    tag === 'profanity'
                      ? 'bg-pink-500 text-white'
                      : tag === 'mercately'
                      ? 'bg-green-500 text-white'
                      : tag === 'hand-off'
                      ? 'bg-amber-300 text-black'
                      : tag === 'internal_check'
                      ? 'bg-orange-500 text-white'
                      : tag === 'extraordinario'
                      ? 'bg-pink-200 text-black'
                      : tag === 'pregunta'
                      ? 'bg-purple-500 text-white'
                      : tag === 'cotiza'
                      ? 'bg-green-500 text-white'
                      : tag === 'WA'
                      ? 'bg-blue-500 text-white'
                      : tag.startsWith('internal')
                      ? 'bg-orange-500 text-white'
                      : tag === 'seguridad'
                      ? 'bg-black text-white'
                      : tag === 'Uber'
                      ? 'bg-blue-200 text-black'
                      : tag === 'Particular'
                      ? 'bg-blue-200 text-black'
                      : tag === 'Plataforma'
                      ? 'bg-blue-200 text-black'
                      : tag === 'finalizado'
                      ? 'bg-yellow-200 text-black'
                      : tag.startsWith('E_')
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-300 text-gray-700'
                  }`}
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Menu for delete and add tag */}
            <div
              className="absolute top-0 right-2 z-30"
              ref={(el) => (menuRefs.current[conversation.id] = el)}
            >
              <button
                className="p-2 rounded-md hover:bg-gray-300 focus:outline-none"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMenu(conversation.id);
                }}
              >
                <Bars3Icon className="h-5 w-5 text-gray-600" />
              </button>
              {menuOpen === conversation.id && (
                <div className="absolute right-0 mt-2 w-24 bg-white border border-gray-200 rounded-md shadow-lg">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick('Delete', conversation.id);
                    }}
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 w-full text-left"
                  >
                    Delete
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick('Add Tag', conversation.id);
                    }}
                    className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 w-full text-left"
                  >
                    Add Tag
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Dialogs for delete and add tag */}
      {dialogOpen && dialogType === 'Delete' && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
          <div className="bg-white rounded-lg p-4 shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Are you sure you want to delete?</h3>
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Confirm
              </button>
              <button
                onClick={() => setDialogOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {dialogOpen && dialogType === 'Add Tag' && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
          <div className="bg-white rounded-lg p-4 shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Select a Tag</h3>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md mb-4"
            >
              <option value="">Select a tag</option>
              <option value="internal">internal</option>
              <option value="WA">WA</option>
              <option value="cotiza">cotiza</option>
              <option value="pregunta">pregunta</option>
              <option value="profanity">profanity</option>
              <option value="E_precio">E_precio</option>
              <option value="E_API">E_API</option>
              <option value="E_auto">E_auto</option>
              <option value="E_eval">E_eval</option>
              <option value="E_RAG">E_RAG</option>
              <option value="seguridad">seguridad</option>
              <option value="extraordinario">extraordinario</option>
              <option value="Uber">Uber</option>
              <option value="Particular">Particular</option>
              <option value="Plataforma">Plataforma</option>
              <option value="finalizado">finalizado</option>
              <option value="mercately">mercately</option>
              <option value="hand-off">hand-off</option>
            </select>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setDialogOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTagConfirm}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                disabled={!selectedTag}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationList;
